import { Tag } from "features/shared/types/tag";
import { emptySubProductPropertyValue } from "../constants/templates";
import { Product } from "../types/product";
import { PropertyType, SubProduct } from "../types/subproduct";
import { TableColumn } from "../types/subproductTable";

interface FormProps {
    fields: Product;
    products: Product[];
    tags: Tag[];
    isUpdating?: boolean;
}

export const verifyForm = ({ fields, products, tags, isUpdating }: FormProps) =>
    new Promise(
        (resolve: (res: string) => void, reject: (err: string) => void) => {
            if (
                !isUpdating &&
                products.find((product) => product.id === fields.id)
            ) {
                reject(
                    `Product ID '${fields.id}' already exists. Please find a new one.`
                );
            }

            if (!fields.id) reject("Provide a product number");
            if (
                fields.tableIds.length < 1 &&
                Object.values(fields.tables).find(
                    (table) => table.columns.length < 1
                )
            )
                reject("Provide at least one table with information");
            if (!fields.properties.name?.en)
                reject("English name not provided");
            if (!fields.properties.name?.th) reject("Thai name not provided");

            if (fields.tags.length) {
                const tagKeys = tags.map((tag) => tag.key);

                fields.tags.forEach((tag) => {
                    if (!tagKeys.includes(tag))
                        reject(`Tag key '${tag}' does not exist!`);
                });
            }

            resolve("Success");
        }
    );

interface PropertyFormProps {
    fields: TableColumn;
    originalAccessor: string;
    columns: TableColumn[];
}

export const verifyPropertyForm = ({
    fields,
    originalAccessor,
    columns,
}: PropertyFormProps) =>
    new Promise(
        (resolve: (res: string) => void, reject: (err: string) => void) => {
            if (!fields.accessor) reject("Accessor not provided");
            if (
                originalAccessor !== fields.accessor &&
                (columns
                    .map((propertyColumn) => propertyColumn.accessor)
                    .includes(fields.accessor) ||
                    fields.isFixed)
            )
                reject(`Accessor '${fields.accessor}' already exists`);
            if (!fields.type) reject("Missing property type");

            resolve("Success");
        }
    );

interface SubProductFormProps {
    fields: SubProduct;
    originalId: string;
    rows: string[];
}

export const verifySubProductForm = ({
    fields,
    originalId,
    rows,
}: SubProductFormProps) =>
    new Promise(
        (resolve: (res: string) => void, reject: (err: string) => void) => {
            if (!fields.id) reject("ID not provided");
            if (originalId !== fields.id && rows.includes(fields.id))
                reject(`ID '${fields.id}' already exists`);
            if (!fields.name.en) reject("English name not provided");
            if (!fields.name.th) reject("Thai name not provided");

            resolve("Success");
        }
    );

export const getEmptyPropertyFromType = (propertyType: PropertyType) => {
    switch (propertyType) {
        case PropertyType.Text:
            return {
                ...emptySubProductPropertyValue.textProperty,
            };
        case PropertyType.FileDownload:
            return emptySubProductPropertyValue.fileDownloadProperty;
        case PropertyType.Image:
            return emptySubProductPropertyValue.imageProperty;
        case PropertyType.PromotionTags:
            return emptySubProductPropertyValue.promotionTagProperty;
    }
};
